import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { PixiPlugin } from "gsap/PixiPlugin";
import * as PIXI from "pixi.js";
import { BitmapText } from "pixi.js";
import { Easing, Tween } from "tweedle.js";
import { isSuperQuickBtnOnGlobal } from "../../config/GlobalVariableConfig";
import { DisplayObject, IDisplayObject } from "../../core/displays/DisplayObject";
import { GameConstants } from "../../gameClientEvents/GameConstants";

gsap.registerPlugin(PixiPlugin, MotionPathPlugin);
PixiPlugin.registerPIXI(PIXI);

export class WinMeterContainer extends DisplayObject {
    private multiplierTextInstances: BitmapText[] = [];
    private labelName: string = "multiplier_";
    private symbolList: Spine[] = [];
    private centerOffset: number = 110;
    private multiplieryOffset: number = 10;
    private effectAnim: Spine;

    constructor(config: IDisplayObject) {
        super(config);
        this.effectAnim = Spine.from({
            skeleton: "particleEffectSkel",
            atlas: "particleEffectAtlas",
        });
        this.effectAnim.visible = false;
        this.effectAnim.label = "particleEffect";
        this.addChild(this.effectAnim);
    }

    public setupMeter(label: string = "0", multiplier: number = 1) {
        this.symbolList = [];
        this.multiplierTextInstances = [];
        const multVal = multiplier;
        const multText = new BitmapText({
            text: `${multVal}x`,
            style: {
                fontFamily: 'symbol_multiplier',
                fontSize: 50
            }
        });
        multText.label = this.labelName + label;
        multText.anchor = 0.5;
        multText.visible = false;
        this.multiplierTextInstances.push(multText);
        multText.scale = 0;
        this.addChild(multText);
    }

    public revealMultiplier(id: string, multiplier: number, symbol: Spine, symId: number = 0) {
        let multiplierInst = this.getChildByLabel(this.labelName + id) as BitmapText;
        multiplierInst.style.fontSize = (symId === GameConstants.WINDMILL_SYMBOL_ID) ? 100 : 50;
        multiplierInst.text = `${multiplier}x`;
        multiplierInst.scale = 1;
        multiplierInst.visible = true;

        (symbol) && (symbol.addSlotObject("multiplier", multiplierInst));
        this.symbolList.push(symbol);
    }

    public addMultiplierTravelAnimationSS(multText: BitmapText, target: BitmapText, callback: () => void) {
        const isQuick = isSuperQuickBtnOnGlobal;
        const windUpTime = isQuick ? 150 : 250;
        const travelTime = isQuick ? 200 : 350;
        const impactTime = 200;

        const flyText = new BitmapText({
            text: multText.text,
            style: multText.style
        });
        flyText.anchor.set(0.5);
        this.addChild(flyText);

        const startPos = this.toLocal(multText.getGlobalPosition());
        const endPos = this.toLocal(target.getGlobalPosition());
        flyText.position.set(startPos.x, startPos.y);

        const pullBackX = startPos.x - (endPos.x - startPos.x) * 0.15;
        const pullBackY = startPos.y - (endPos.y - startPos.y) * 0.15;

        new Tween(flyText)
            .to({
                x: pullBackX,
                y: pullBackY,
                scale: { x: 1.5, y: 1.5 }
            }, windUpTime)
            .easing(Easing.Back.Out)
            .onComplete(() => {
                new Tween(flyText)
                    .to({
                        x: endPos.x,
                        y: endPos.y,
                        scale: { x: 0.7, y: 0.7 }
                    }, travelTime)
                    .easing(Easing.Exponential.In)
                    .onComplete(() => {
                        this.removeChild(flyText);
                        flyText.destroy();
                        if (callback) callback();

                        new Tween(target.scale)
                            .to({ x: 1.4, y: 1.4 }, impactTime * 0.4)
                            .yoyo(true)
                            .repeat(1)
                            .easing(Easing.Quadratic.Out)
                            .onComplete(() => {
                                target.scale.set(1);
                            })
                            .start();
                    })
                    .start();
            })
            .start();
    }

    public addMultiplierTravelAnimationGS(multText: BitmapText, target: BitmapText, callback: () => void) {
        const flyText = new BitmapText({ text: multText.text, style: multText.style });
        flyText.anchor.set(0.5);
        this.addChild(flyText);

        const start = this.toLocal(multText.getGlobalPosition());
        const end = this.toLocal(target.getGlobalPosition());

        const peakY = Math.min(start.y, end.y) - 150;

        flyText.position.set(start.x, start.y);

        new Tween(flyText)
            .to({
                x: [start.x + (end.x - start.x) * 0.5, end.x], // bezier like path
                y: [peakY, end.y],
                scale: { x: 1.2, y: 1.2 }
            }, isSuperQuickBtnOnGlobal ? 300 : 600)
            .easing(Easing.Quadratic.Out)
            .onComplete(() => {
                this.removeChild(flyText);
                flyText.destroy();
                callback?.();

                new Tween(target.scale).to({ x: 1.3, y: 1.3 }, 100).yoyo(true).repeat(1).start();
            })
            .start();
    }

    public addMultiplierTravelAnimation_Add2(multText: BitmapText, target: BitmapText, callback: () => void) {
        const fly = new BitmapText({ text: multText.text, style: multText.style });
        fly.anchor.set(0.5);
        this.addChild(fly);

        const start = this.toLocal(multText.getGlobalPosition());
        const end = this.toLocal(target.getGlobalPosition());

        fly.position.set(start.x, start.y);

        const midX = start.x + (end.x - start.x) * 0.4;
        const midY = Math.min(start.y, end.y) - 180;

        gsap.timeline({
            onComplete: () => {
                this.removeChild(fly);
                fly.destroy();
                callback?.();

                gsap.to(target, { pixi: { scale: 1.4 }, yoyo: true, repeat: 1, duration: 0.12 });
            }
        })
            .to(fly, {
                pixi: { scale: 1.6 },
                duration: 0.12,
                ease: "back.out(3)"
            })
            .to(fly, {
                pixi: {
                    x: midX,
                    y: midY,
                    rotation: Math.PI * 0.8
                },
                duration: 0.22,
                ease: "power2.out"
            })
            .to(fly, {
                pixi: {
                    x: end.x,
                    y: end.y,
                    scale: 0.6,
                    rotation: Math.PI * 2
                },
                duration: 0.28,
                ease: "expo.in"
            });
    }

    public addMultiplierTravelAnimation_Add1(multText: BitmapText, target: BitmapText, callback?: () => void) {
        const fly = new BitmapText({ text: multText.text, style: multText.style });
        fly.anchor.set(0.5);
        this.addChild(fly);

        const start = this.toLocal(multText.getGlobalPosition());
        const end = this.toLocal(target.getGlobalPosition());

        const arcHeight = -100 - (Math.random() * 150);
        const midX = (start.x + end.x) * 0.5;
        const midY = Math.min(start.y, end.y) + arcHeight;

        gsap.set(fly, { x: start.x, y: start.y, alpha: 0, pixi: { scale: 0.5 } });

        const tl = gsap.timeline({
            onComplete: () => {
                this.removeChild(fly);
                fly.destroy();
                callback?.();

                gsap.fromTo(target, { pixi: { scale: 0.8 } }, {
                    pixi: { scale: 1 },
                    duration: 0.4,
                    ease: "back.out(4)"
                });
            }
        });

        tl.to(fly, { alpha: 1, pixi: { scale: 1.4 }, duration: 0.15, ease: "power2.out" })
            .to(fly, {
                duration: 0.6,
                ease: "sine.inOut",

                motionPath: {
                    path: [
                        { x: start.x, y: start.y },
                        { x: midX, y: midY },
                        { x: end.x, y: end.y }
                    ],
                    curviness: 1.5
                },

                pixi: {
                    rotation: 360
                }
            }, "-=0.1")
            .to(fly, { pixi: { scale: 0.8 }, duration: 0.2 }, "-=0.2");
    }

    public addMultiplierTravelAnimation_AddMP(multText: BitmapText, target: BitmapText, callback?: () => void) {
        const fly = new BitmapText({ text: multText.text, style: multText.style });
        fly.anchor.set(0.5);
        this.addChild(fly);

        const start = this.toLocal(multText.getGlobalPosition());
        const end = this.toLocal(target.getGlobalPosition());

        const arcHeight = -100 - (Math.random() * 150);
        const midX = (start.x + end.x) * 0.5;
        const midY = Math.min(start.y, end.y) + arcHeight;


        gsap.set(fly, { x: start.x, y: start.y, alpha: 0, scale: 0.5 });

        const state = { t: 0 };

        const tl = gsap.timeline({
            onComplete: () => {
                this.removeChild(fly);
                fly.destroy();
                callback?.();
                gsap.fromTo(target.scale, { x: 0.8, y: 0.8 }, { x: 1, y: 1, duration: 0.4, ease: "back.out(4)" });
            }
        });


        tl.to(fly, { alpha: 1, scale: 1.4, duration: 0.15, ease: "power2.out" });


        tl.to(state, {
            t: 1,
            duration: 0.6,
            ease: "sine.inOut",
            onUpdate: () => {
                const t = state.t;
                const invT = 1 - t;
                fly.x = (invT * invT * start.x) + (2 * invT * t * midX) + (t * t * end.x);
                fly.y = (invT * invT * start.y) + (2 * invT * t * midY) + (t * t * end.y);

                fly.rotation = t * Math.PI * 2;
            }
        }, "-=0.1");


        tl.to(fly, { scale: 0.8, duration: 0.2 }, "-=0.2");
    }

    public addMultiplierTravelAnimation_Add(multText: BitmapText, target: BitmapText, callback?: () => void) {
        const fly = new BitmapText({ text: multText.text, style: multText.style });
        fly.anchor.set(0.5);
        this.addChild(fly);

        const start = this.toLocal(multText.getGlobalPosition());
        const end = this.toLocal(target.getGlobalPosition());

        fly.position.set(start.x, start.y);

        const wave = { t: 0 };

        const amplitude = 22;
        const frequency = 3;
        const lift = 60;

        gsap.timeline({
            onComplete: () => {
                this.removeChild(fly);
                fly.destroy();
                callback?.();

                gsap.fromTo(
                    target.scale,
                    { x: 1, y: 1 },
                    { x: 1.1, y: 1.1, duration: 0.18, yoyo: true, repeat: 1, ease: "sine.out" }
                );
            }
        })
            .to(wave, {
                t: 1,
                duration: 0.85,
                ease: "sine.inOut",
                onUpdate: () => {
                    const waveX = Math.sin(wave.t * Math.PI * frequency) * amplitude;
                    const liftY = Math.sin(wave.t * Math.PI) * lift;

                    fly.x = start.x + (end.x - start.x) * wave.t + waveX;
                    fly.y = start.y + (end.y - start.y) * wave.t - liftY;

                    fly.rotation = Math.sin(wave.t * Math.PI) * 0.15;
                    fly.scale.set(1 - wave.t * 0.2);
                }
            });
    }

    public addMultiplierTravelAnimation_Collect2(multText: BitmapText, target: BitmapText, callback: () => void) {
        const fly = new BitmapText({ text: multText.text, style: multText.style });
        fly.anchor.set(0.5);
        this.addChild(fly);

        const start = this.toLocal(multText.getGlobalPosition());
        const end = this.toLocal(target.getGlobalPosition());

        fly.position.set(start.x, start.y);

        const orbitX = (start.x + end.x) * 0.5 + 120;
        const orbitY = (start.y + end.y) * 0.5 - 120;

        gsap.timeline({
            onComplete: () => {
                this.removeChild(fly);
                fly.destroy();
                callback?.();

                gsap.fromTo(target, { pixi: { scale: 1.3 } }, {
                    pixi: { scale: 1 },
                    duration: 0.2,
                    ease: "elastic.out(1, 0.4)"
                });
            }
        })

            .to(fly, {
                pixi: { x: orbitX, y: orbitY, rotation: Math.PI },
                duration: 0.25,
                ease: "power2.out"
            })

            .to(fly, {
                pixi: {
                    x: end.x,
                    y: end.y,
                    scale: 0.2,
                    rotation: Math.PI * 3
                },
                duration: 0.3,
                ease: "power4.in"
            });
    }

    public addMultiplierTravelAnimation_Collect1(multText: BitmapText, target: BitmapText, callback: () => void) {
        const fly = new BitmapText({ text: multText.text, style: multText.style });
        fly.anchor.set(0.5);
        this.addChild(fly);

        const start = this.toLocal(multText.getGlobalPosition());
        const end = this.toLocal(target.getGlobalPosition());

        gsap.set(fly, { x: start.x, y: start.y });


        const angle = Math.atan2(end.y - start.y, end.x - start.x) * (180 / Math.PI);

        const tl = gsap.timeline({
            onComplete: () => {
                this.removeChild(fly);
                fly.destroy();
                callback?.();


                gsap.timeline()
                    .to(target.scale, { x: 1.4, y: 0.6, duration: 0.1 })
                    .to(target.scale, { x: 1, y: 1, duration: 0.4, ease: "elastic.out(1, 0.3)" });
            }
        });

        tl.to(fly, {
            pixi: { scaleX: 1.8, scaleY: 0.6, rotation: angle },
            duration: 0.2,
            ease: "power2.in"
        })
            .to(fly, {
                pixi: {
                    x: end.x,
                    y: end.y,
                    scaleX: 0.2,
                    scaleY: 0.2
                },
                duration: 0.3,
                ease: "expo.in"
            });
    }

    public addMultiplierTravelAnimation_Collect(multText: BitmapText, target: BitmapText, callback?: () => void) {
        const fly = new BitmapText({ text: multText.text, style: multText.style });
        fly.anchor.set(0.5);
        fly.visible = false;
        this.addChild(fly);

        const start = this.toLocal(multText.getGlobalPosition());
        const end = this.toLocal(target.getGlobalPosition());
        end.y += this.multiplieryOffset;

        const waveAmplitude = 40;
        const waveFrequency = 1.5;


        const animationState = { p: 0 };

        gsap.to(animationState, {
            p: 1,
            duration: 0.8,
            ease: "sine.inOut",
            onUpdate: () => {
                fly.visible = true;
                const p = animationState.p;


                const curX = start.x + (end.x - start.x) * p;
                const curY = start.y + (end.y - start.y) * p;


                const wave = Math.sin(p * Math.PI * waveFrequency) * waveAmplitude;

                fly.x = curX;
                fly.y = curY + wave;


                const s = 1 - (0.25 * p);
                fly.scale.set(s);
            },
            onComplete: () => {
                this.removeChild(fly);
                fly.destroy();
                callback?.();

                gsap.fromTo(target.scale,
                    { x: 1, y: 1 },
                    { x: 1.15, y: 1.15, duration: 0.18, yoyo: true, repeat: 1, ease: "sine.out" }
                );
            }
        });
    }

    public collectAllMultipliersSpiral(
        texts: BitmapText[],
        target: Spine,
        callback?: () => void
    ) {
        const duration = 0.9;
        const center = this.toLocal(target.getGlobalPosition());
        center.y -= this.centerOffset;

        texts.forEach((txt, i) => {
            const angleOffset = i * 0.6;
            const radiusStart = 160 + i * 10;

            const proxy = {
                angle: angleOffset,
                radius: radiusStart
            };

            const fly = new BitmapText({
                text: txt.text,
                style: txt.style
            });

            fly.anchor.set(0.5);

            // ✅ Spawn directly in spiral start position
            fly.x = center.x + Math.cos(proxy.angle) * proxy.radius;
            fly.y = center.y + Math.sin(proxy.angle) * proxy.radius;

            fly.scale.set(0.6);
            fly.alpha = 1;

            this.addChild(fly);

            if (txt.label === "accumWinText") {
                txt.visible = false;
            }

            gsap.to(proxy, {
                angle: angleOffset + Math.PI * 2,
                radius: 0,
                duration,
                ease: "sine.inOut",
                onUpdate: () => {
                    fly.x = center.x + Math.cos(proxy.angle) * proxy.radius;
                    fly.y = center.y + Math.sin(proxy.angle) * proxy.radius;

                    fly.scale.set(
                        0.6 + (1 - proxy.radius / radiusStart) * 0.4
                    );

                    fly.alpha = proxy.radius / radiusStart;
                },
                onComplete: () => {
                    this.removeChild(fly);
                    fly.destroy();

                    if (i === texts.length - 1) {
                        callback?.();
                    }
                }
            });
        });
    }


    public showTotalWinMeter(target: Spine, amount: number, callback?: () => void) {
        const center = this.toLocal(target.getGlobalPosition());
        center.y -= this.centerOffset;

        const totalText = new BitmapText({
            text: `${amount.toFixed(2)}`, style: {
                fontFamily: 'symbol_multiplier',
                fontSize: 150,
            }
        });

        totalText.anchor.set(0.5);
        totalText.visible = false;
        totalText.position = center;
        this.effectAnim.position = center;

        this.addChild(totalText);

        totalText.scale.set(0.85);
        totalText.alpha = 0;
        // totalText.y = center.y + 20; // slight offset for smooth lift

        const tl = gsap.timeline();

        tl.to(totalText, {
            alpha: 1,
            y: center.y,
            duration: 0.3,
            ease: "power2.out",
            onStart: () => {
                totalText.visible = true;
                this.effectAnim.visible = true;
                this.effectAnim.state.setAnimation(0, "in", false);
            }
        })
            .to(totalText.scale, {
                x: 1.08,
                y: 1.08,
                duration: 0.25,
                ease: "power3.out"
            }, "<")
            .to(totalText.scale, {
                x: 1,
                y: 1,
                duration: 0.2,
                ease: "power2.out"
            })
            // Exit
            // .to(totalText.scale, {
            //     x: 1.2,
            //     y: 1.2,
            //     duration: 0.15,
            //     ease: "power2.in"
            // })
            .to(totalText.scale, {
                x: 0,
                y: 0,
                duration: 0.15,
                ease: "power3.in"
            }, "+=0.6")
            .add(() => {
                this.effectAnim.state.setAnimation(0, "out", false);
            }, "-=0.3")
            .to(totalText, {
                alpha: 0,
                y: center.y,
                duration: 0.5,
                ease: "power2.in",
                // onStart: () => {
                //     this.effectAnim.state.setAnimation(0, "out", false);
                // },
                onComplete: () => {
                    this.removeChild(totalText);
                    totalText.destroy();

                    this.effectAnim.visible = false;
                    this.effectAnim.state.clearTracks();

                    callback?.();
                }
            });


    }

    public resetMultipliers() {
        this.symbolList && this.symbolList.forEach((symbol) => {
            let obj = symbol?.getSlotObject("multiplier");
            obj?.destroy();
            (obj) && (symbol.removeSlotObject("multiplier"));
        });
        this.symbolList = [];
    }

    public override destroy() {
        super.destroy();
        this.symbolList = [];
        this.multiplierTextInstances = [];
    }
}